import { useEffect, useState, type FormEvent } from 'react';
import { authErrorMessage, useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { useGo } from '../components/Nav';
import { GoogleIcon } from '../components/icons';
import { Banner, Button, Input, Spinner } from '../components/ui';
import { firebaseConfigured } from '../lib/env';

/**
 * /login. Sign in only: there is nowhere to sign up, because an admin account
 * is made by another admin. Whoever signs in here is then checked against the
 * admins table by the gate in router.tsx, which shows the "not an admin"
 * screen to everyone else.
 */

function nextPath(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const { user, loading, signInWithGoogle, signInWithEmail, sendReset } = useAuth();
  const go = useGo();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'email' | null>(null);

  useEffect(() => {
    if (!loading && user) go(nextPath(), { replace: true });
  }, [go, loading, user]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy('email');
    signInWithEmail(email, password)
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(null));
  };

  const google = () => {
    setError(null);
    setBusy('google');
    signInWithGoogle()
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(null));
  };

  const reset = () => {
    if (!email.trim()) {
      setError('Enter your email address first, then ask for a reset link.');
      return;
    }
    setError(null);
    sendReset(email)
      .then(() => setNotice(`If there is an account for ${email.trim()}, a reset link is on its way.`))
      .catch((cause: unknown) => setError(authErrorMessage(cause)));
  };

  if (loading || user) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-2.5 px-4 sm:px-6">
          <Wordmark />
          <span className="text-caption uppercase tracking-[0.12em] text-chalk-faint">Admin</span>
        </div>
        <ClapperRule />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <h1 className="text-h2 text-chalk">Sign in</h1>
          <p className="mt-1.5 text-body text-chalk-dim">With the account another admin let in.</p>

          {!firebaseConfigured ? (
            <div className="mt-5">
              <Banner tone="danger" title="Sign-in is not configured">
                This build has no Firebase credentials. Run <code>yarn ssm:fetch:local</code> (or the
                environment you meant) before starting the dev server.
              </Banner>
            </div>
          ) : null}

          <div className="mt-6 space-y-5">
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              busy={busy === 'google'}
              disabled={!firebaseConfigured || busy !== null}
              onClick={google}
            >
              {busy !== 'google' ? <GoogleIcon size={16} /> : null}
              Continue with Google
            </Button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-hairline" />
              <span className="text-micro uppercase tracking-[0.1em] text-chalk-faint">or</span>
              <div className="h-px flex-1 bg-hairline" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />

              {error ? <Banner tone="danger">{error}</Banner> : null}
              {notice ? <Banner tone="info">{notice}</Banner> : null}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                busy={busy === 'email'}
                disabled={!firebaseConfigured || busy !== null}
              >
                Sign in
              </Button>
            </form>

            <button
              type="button"
              onClick={reset}
              className="text-caption text-chalk-faint underline-offset-2 hover:text-chalk-dim hover:underline"
            >
              Forgot your password?
            </button>
          </div>

          <p className="mt-6 text-caption text-chalk-faint">
            Not an admin yet? Ask one to add you from the Admins page.
          </p>
        </div>
      </main>
    </div>
  );
}
